import { useQuery } from "@tanstack/react-query"
import { CompanyTradeDataBOLs, TradeDataMarketAnalytics } from "src/model/tradeData"
import { TRADE_DATA_CACHE_TIME, TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME } from "src/utils/constants/config"
import { TradeDataWithoutHSCodeService } from './../api/tradeDataWithoutHSCode'

const TRADE_DATA_NO_HS_CODE_ANALYTICS = 'TRADE_DATA_NO_HS_CODE_ANALYTICS'
const TRADE_DATA_NO_HS_CODE_BOLS = 'TRADE_DATA_NO_HS_CODE_BOLS'
const TRADE_DATA_NO_HS_CODE_LIST_HS_CODE_CHART = 'TRADE_DATA_NO_HS_CODE_LIST_HS_CODE_CHART'
const TRADE_DATA_NO_HS_CODE_LIST_HS_CODE = 'TRADE_DATA_NO_HS_CODE_LIST_HS_CODE'
const TRADE_DATA_NO_HS_CODE_BUYER_SUPPLIER_WORLD_MAP = 'TRADE_DATA_NO_HS_CODE_BUYER_SUPPLIER_WORLD_MAP'
const TRADE_DATA_NO_HS_CODE_BUYER_SUPPLIER_CHART = 'TRADE_DATA_NO_HS_CODE_BUYER_SUPPLIER_CHART'
const TRADE_DATA_NO_HS_CODE_BUYER_SUPPLIER_LIST = 'TRADE_DATA_NO_HS_CODE_BUYER_SUPPLIER_LIST'
const TRADE_DATA_NO_HS_CODE_COMPETITOR_WORLD_MAP = 'TRADE_DATA_NO_HS_CODE_COMPETITOR_WORLD_MAP'
const TRADE_DATA_NO_HS_CODE_COMPETITOR_CHART = 'TRADE_DATA_NO_HS_CODE_COMPETITOR_CHART'
const TRADE_DATA_NO_HS_CODE_COMPETITOR_LIST = 'TRADE_DATA_NO_HS_CODE_COMPETITOR_LIST'
const TRADE_DATA_NO_HS_CODE_PORT_WORLD_MAP = 'TRADE_DATA_NO_HS_CODE_PORT_WORLD_MAP'
const TRADE_DATA_NO_HS_CODE_PORT_CHART = 'TRADE_DATA_NO_HS_CODE_PORT_CHART'
const TRADE_DATA_NO_HS_CODE_PORT_LIST = 'TRADE_DATA_NO_HS_CODE_PORT_LIST'
const TRADE_DATA_NO_HS_CODE_TRADING_AREA_WORLD_MAP = 'TRADE_DATA_NO_HS_CODE_TRADING_AREA_WORLD_MAP'
const TRADE_DATA_NO_HS_CODE_TRADING_AREA_LIST = 'TRADE_DATA_NO_HS_CODE_TRADING_AREA_LIST'

export const useQueryGetTradeDataMarketAnalyticsChart = (query: Record<string, string | number>) => {
  return useQuery({
    queryKey: [TRADE_DATA_NO_HS_CODE_ANALYTICS, query],
    queryFn: () => TradeDataWithoutHSCodeService.getCompanyTransactionAnalytics(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradeDataMarketAnalytics(),
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  })
}

export const useQueryCompanyTradeDataBOLS = (query: Record<string, string | number>) => {
  return useQuery({
    queryKey: [TRADE_DATA_NO_HS_CODE_BOLS, query],
    queryFn: () => TradeDataWithoutHSCodeService.getCompanyTransactions(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyTradeDataBOLs(),
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  })
}

export const useQueryCompanyTradeDataHSCode = (params: Record<string, any>) => {
  const { data, isFetching } = useQuery({
    queryKey: [TRADE_DATA_NO_HS_CODE_LIST_HS_CODE_CHART, params],
    queryFn: () => TradeDataWithoutHSCodeService.getCompanyTradeDataHSCodeChart(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });

  return {
    data,
    isFetching,
  };
};

export const useQueryCompanyTradeDataHSCodeList = (params: Record<string, any>) => {
  const { data, isFetching } = useQuery({
    queryKey: [TRADE_DATA_NO_HS_CODE_LIST_HS_CODE, params],
    queryFn: () => TradeDataWithoutHSCodeService.getCompanyTradeDataHSCodeList(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });

  return {
    data,
    isFetching,
  };
};

export const useQueryCTDBuyerSupplierCountryWorldMap = (params: Record<string, any>) => {
  const { data, isFetching } = useQuery({
    queryKey: [TRADE_DATA_NO_HS_CODE_BUYER_SUPPLIER_WORLD_MAP, params],
    queryFn: () => TradeDataWithoutHSCodeService.getCTDBuyerSupplierCountryWorldMap(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });
  return {
    data,
    isFetching,
  };
}
export const useQueryCTDBuyerSupplierCountryChart = (params: Record<string, any>) => {
  const { data, isFetching } = useQuery({
    queryKey: [TRADE_DATA_NO_HS_CODE_BUYER_SUPPLIER_CHART, params],
    queryFn: () => TradeDataWithoutHSCodeService.getCTDBuyerSupplierCountryChart(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });
  return {
    data,
    isFetching,
  };
}
export const useQueryCTDBuyerSupplierCountryList = (params: Record<string, any>) => {
  const { data, isFetching } = useQuery({
    queryKey: [TRADE_DATA_NO_HS_CODE_BUYER_SUPPLIER_LIST, params],
    queryFn: () => TradeDataWithoutHSCodeService.getCTDBuyerSupplierCountryList(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });
  return {
    data,
    isFetching,
  };
}
export const useQueryCTDCompetitorWorldMap = (params: Record<string, any>) => {
  const { data, isFetching } = useQuery({
    queryKey: [TRADE_DATA_NO_HS_CODE_COMPETITOR_WORLD_MAP, params],
    queryFn: () => TradeDataWithoutHSCodeService.getCTDCompetitorWorldMap(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });
  return {
    data,
    isFetching,
  };
}
export const useQueryCTDCompetitorChart = (params: Record<string, any>) => {
  const { data, isFetching } = useQuery({
    queryKey: [TRADE_DATA_NO_HS_CODE_COMPETITOR_CHART, params],
    queryFn: () => TradeDataWithoutHSCodeService.getCTDCompetitorChart(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });
  return {
    data,
    isFetching,
  };
}
export const useQueryCTDCompetitorList = (params: Record<string, any>) => {
  const { data, isFetching } = useQuery({
    queryKey: [TRADE_DATA_NO_HS_CODE_COMPETITOR_LIST, params],
    queryFn: () => TradeDataWithoutHSCodeService.getCTDCompetitorList(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });
  return {
    data,
    isFetching,
  };
}
export const useQueryCTDPortWorldMap = (params: Record<string, any>) => {
  const { data, isFetching } = useQuery({
    queryKey: [TRADE_DATA_NO_HS_CODE_PORT_WORLD_MAP, params],
    queryFn: () => TradeDataWithoutHSCodeService.getCTDPortWorldMap(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });
  return {
    data,
    isFetching,
  };
}
export const useQueryCTDPortChart = (params: Record<string, any>) => {
  const { data, isFetching } = useQuery({
    queryKey: [TRADE_DATA_NO_HS_CODE_PORT_CHART, params],
    queryFn: () => TradeDataWithoutHSCodeService.getCTDPortChart(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });
  return {
    data,
    isFetching,
  };
}
export const useQueryCTDPortList = (params: Record<string, any>) => {
  const { data, isFetching } = useQuery({
    queryKey: [TRADE_DATA_NO_HS_CODE_PORT_LIST, params],
    queryFn: () => TradeDataWithoutHSCodeService.getCTDPortList(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });
  return {
    data,
    isFetching,
  };
}
export const useQueryCTDTradingAreaWorldMap = (params: Record<string, any>) => {
  const { data, isFetching } = useQuery({
    queryKey: [TRADE_DATA_NO_HS_CODE_TRADING_AREA_WORLD_MAP, params],
    queryFn: () => TradeDataWithoutHSCodeService.getCTDTradingAreaWorldMap(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });
  return {
    data,
    isFetching,
  };
}
export const useQueryCTDTradingAreaList = (params: Record<string, any>) => {
  const { data, isFetching } = useQuery({
    queryKey: [TRADE_DATA_NO_HS_CODE_TRADING_AREA_LIST, params],
    queryFn: () => TradeDataWithoutHSCodeService.getCTDTradingAreaList(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });
  return {
    data,
    isFetching,
  };
}
